import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Soft-brutalist tinted container — no border, no card chrome.
 * Pure color block as section boundary, generous padding.
 *
 * Studio language: each section gets a distinct soft-tinted region.
 * Use instead of <SectionCard> when you want region-by-color, not
 * border-by-card.
 */
export type ColorBlockTint =
  | "peach"
  | "sage"
  | "blue"
  | "cream"
  | "lilac"
  | "rose"
  | "stone"
  | "ink";

const TINTS: Record<ColorBlockTint, string> = {
  peach: "bg-orange-100/70 text-stone-900",
  sage: "bg-emerald-100/55 text-stone-900",
  blue: "bg-sky-100/70 text-stone-900",
  cream: "bg-amber-100/55 text-stone-900",
  lilac: "bg-violet-100/60 text-stone-900",
  rose: "bg-rose-100/60 text-stone-900",
  stone: "bg-stone-100/80 text-stone-900",
  ink: "bg-stone-900 text-stone-50",
};

const PADDING = {
  sm: "px-5 py-5 sm:px-6 sm:py-6",
  md: "px-6 py-7 sm:px-8 sm:py-8",
  lg: "px-7 py-9 sm:px-10 sm:py-12",
} as const;

export type ColorBlockProps = {
  tint?: ColorBlockTint;
  size?: keyof typeof PADDING;
  /** Small uppercase kicker pinned to the top */
  eyebrow?: string;
  /** Optional title shown beneath the eyebrow */
  title?: string;
  /** Optional inline-end node beside the eyebrow */
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
};

export function ColorBlock({
  tint = "cream",
  size = "md",
  eyebrow,
  title,
  aside,
  children,
  className,
}: ColorBlockProps) {
  return (
    <section
      className={cn(
        "rounded-[28px]",
        TINTS[tint],
        PADDING[size],
        className,
      )}
    >
      {(eyebrow || title || aside) && (
        <header
          className={cn(
            "flex items-baseline justify-between gap-4",
            (eyebrow || title) && "mb-5 sm:mb-6",
          )}
        >
          <div className="min-w-0">
            {eyebrow ? (
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] opacity-65">
                {eyebrow}
              </p>
            ) : null}
            {title ? (
              <h2 className="mt-2 text-xl sm:text-2xl font-semibold tracking-tight leading-tight">
                {title}
              </h2>
            ) : null}
          </div>
          {aside ? <div className="shrink-0 text-sm opacity-75">{aside}</div> : null}
        </header>
      )}
      {children}
    </section>
  );
}
